import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL;

const adminFutureGiftApi = axios.create({
  baseURL: `${API_URL}/api/v1/admin`,
});

// Request interceptor to add token
adminFutureGiftApi.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('adminToken');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    
    if (!(config.data instanceof FormData)) {
      config.headers['Content-Type'] = 'application/json';
    }
    
    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor to handle errors
adminFutureGiftApi.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('adminToken');
      localStorage.removeItem('adminUser');
      window.location.href = '/admin/login';
    }
    return Promise.reject(error);
  }
);

const adminFutureGiftService = {
  // ==================== BOX CONFIGURATION ====================
  /**
   * Get box configuration
   */
  getBoxConfig: async () => {
    console.log('📤 [FutureGiftService] Fetching box configuration...');
    try {
      const response = await adminFutureGiftApi.get('/future-gifts/box-config');
      console.log('📥 [FutureGiftService] Box config fetched:', response.data);
      return response.data;
    } catch (error) {
      console.error('💥 [FutureGiftService] Error fetching box config:', error);
      throw error;
    }
  },

  /**
   * Update box configuration
   */
  updateBoxConfig: async (configData) => {
    console.log('📤 [FutureGiftService] Updating box configuration:', configData);
    try {
      const response = await adminFutureGiftApi.put('/future-gifts/box-config', configData);
      console.log('📥 [FutureGiftService] Box config updated:', response.data);
      return response.data;
    } catch (error) {
      console.error('💥 [FutureGiftService] Error updating box config:', error);
      throw error;
    }
  },

  // ==================== CUSTOMIZATION RULES ====================
  /**
   * Get customization rules
   */
  getCustomizationRules: async () => {
    console.log('📤 [FutureGiftService] Fetching customization rules...');
    try {
      const response = await adminFutureGiftApi.get('/future-gifts/customization-rules');
      console.log('📥 [FutureGiftService] Customization rules fetched:', response.data);
      return response.data;
    } catch (error) {
      console.error('💥 [FutureGiftService] Error fetching customization rules:', error);
      throw error;
    }
  },

  /**
   * Update customization rules
   */
  updateCustomizationRules: async (rulesData) => {
    console.log('📤 [FutureGiftService] Updating customization rules:', rulesData);
    try {
      const response = await adminFutureGiftApi.put('/future-gifts/customization-rules', rulesData);
      console.log('📥 [FutureGiftService] Customization rules updated:', response.data);
      return response.data;
    } catch (error) {
      console.error('💥 [FutureGiftService] Error updating customization rules:', error);
      throw error;
    }
  },

  // ==================== ADD-ONS ====================
  /**
   * Get all add-ons
   */
  getAddons: async () => {
    console.log('📤 [FutureGiftService] Fetching add-ons...');
    try {
      const response = await adminFutureGiftApi.get('/future-gifts/addons');
      console.log('📥 [FutureGiftService] Add-ons fetched:', response.data);
      return response.data;
    } catch (error) {
      console.error('💥 [FutureGiftService] Error fetching add-ons:', error);
      throw error;
    }
  },

  /**
   * Get add-on by ID
   */
  getAddonById: async (addonId) => {
    console.log('📤 [FutureGiftService] Fetching add-on:', addonId);
    try {
      const response = await adminFutureGiftApi.get(`/future-gifts/addons/${addonId}`);
      console.log('📥 [FutureGiftService] Add-on fetched:', response.data);
      return response.data;
    } catch (error) {
      console.error('💥 [FutureGiftService] Error fetching add-on:', error);
      throw error;
    }
  },

  /**
   * Create new add-on group
   */
  createAddonGroup: async (addonData) => {
    console.log('📤 [FutureGiftService] Creating add-on group:', addonData);
    try {
      const response = await adminFutureGiftApi.post('/future-gifts/addons', addonData);
      console.log('📥 [FutureGiftService] Add-on group created:', response.data);
      return response.data;
    } catch (error) {
      console.error('💥 [FutureGiftService] Error creating add-on group:', error);
      throw error;
    }
  },

  /**
   * Update add-on group
   */
  updateAddonGroup: async (addonId, addonData) => {
    console.log('📤 [FutureGiftService] Updating add-on group:', addonId);
    try {
      const response = await adminFutureGiftApi.put(`/future-gifts/addons/${addonId}`, addonData);
      console.log('📥 [FutureGiftService] Add-on group updated:', response.data);
      return response.data;
    } catch (error) {
      console.error('💥 [FutureGiftService] Error updating add-on group:', error);
      throw error;
    }
  },

  /**
   * Toggle add-on group enabled status
   */
  toggleAddonGroup: async (addonId, enabled) => {
    console.log('📤 [FutureGiftService] Toggling add-on group:', addonId, enabled);
    try {
      const response = await adminFutureGiftApi.patch(`/future-gifts/addons/${addonId}/toggle`, { enabled });
      console.log('📥 [FutureGiftService] Add-on group toggled:', response.data);
      return response.data;
    } catch (error) {
      console.error('💥 [FutureGiftService] Error toggling add-on group:', error);
      throw error;
    }
  },

  /**
   * Delete add-on group
   */
  deleteAddonGroup: async (addonId) => {
    console.log('📤 [FutureGiftService] Deleting add-on group:', addonId);
    try {
      const response = await adminFutureGiftApi.delete(`/future-gifts/addons/${addonId}`);
      console.log('📥 [FutureGiftService] Add-on group deleted:', response.data);
      return response.data;
    } catch (error) {
      console.error('💥 [FutureGiftService] Error deleting add-on group:', error);
      throw error;
    }
  },

  // ==================== ADD-ON ITEMS ====================
  /**
   * Add item to add-on group
   */
  addAddonItem: async (addonId, itemData) => {
    console.log('📤 [FutureGiftService] Adding item to add-on:', addonId, itemData);
    try {
      const response = await adminFutureGiftApi.post(`/future-gifts/addons/${addonId}/items`, itemData);
      console.log('📥 [FutureGiftService] Add-on item added:', response.data);
      return response.data;
    } catch (error) {
      console.error('💥 [FutureGiftService] Error adding add-on item:', error);
      throw error;
    }
  },

  /**
   * Update add-on item
   */
  updateAddonItem: async (addonId, itemId, itemData) => {
    console.log('📤 [FutureGiftService] Updating add-on item:', addonId, itemId);
    try {
      const response = await adminFutureGiftApi.put(`/future-gifts/addons/${addonId}/items/${itemId}`, itemData);
      console.log('📥 [FutureGiftService] Add-on item updated:', response.data);
      return response.data;
    } catch (error) {
      console.error('💥 [FutureGiftService] Error updating add-on item:', error);
      throw error;
    }
  },

  /**
   * Delete add-on item
   */
  deleteAddonItem: async (addonId, itemId) => {
    console.log('📤 [FutureGiftService] Deleting add-on item:', addonId, itemId);
    try {
      const response = await adminFutureGiftApi.delete(`/future-gifts/addons/${addonId}/items/${itemId}`);
      console.log('📥 [FutureGiftService] Add-on item deleted:', response.data);
      return response.data;
    } catch (error) {
      console.error('💥 [FutureGiftService] Error deleting add-on item:', error);
      throw error;
    }
  },

  // ==================== GIFT TYPES ====================
  /**
   * Get all gift types
   */
  getGiftTypes: async () => {
    console.log('📤 [FutureGiftService] Fetching gift types...');
    try {
      const response = await adminFutureGiftApi.get('/future-gifts/gift-types');
      console.log('📥 [FutureGiftService] Gift types fetched:', response.data);
      return response.data;
    } catch (error) {
      console.error('💥 [FutureGiftService] Error fetching gift types:', error);
      throw error;
    }
  },

  /**
   * Create gift type
   */
  createGiftType: async (giftTypeData) => {
    console.log('📤 [FutureGiftService] Creating gift type:', giftTypeData);
    try {
      const response = await adminFutureGiftApi.post('/future-gifts/gift-types', giftTypeData);
      console.log('📥 [FutureGiftService] Gift type created:', response.data);
      return response.data;
    } catch (error) {
      console.error('💥 [FutureGiftService] Error creating gift type:', error);
      throw error;
    }
  },

  /**
   * Update gift type
   */
  updateGiftType: async (giftTypeId, giftTypeData) => {
    console.log('📤 [FutureGiftService] Updating gift type:', giftTypeId);
    try {
      const response = await adminFutureGiftApi.put(`/future-gifts/gift-types/${giftTypeId}`, giftTypeData);
      console.log('📥 [FutureGiftService] Gift type updated:', response.data);
      return response.data;
    } catch (error) {
      console.error('💥 [FutureGiftService] Error updating gift type:', error);
      throw error;
    }
  },

  /**
   * Toggle gift type enabled status
   */
  toggleGiftType: async (giftTypeId, enabled) => {
    console.log('📤 [FutureGiftService] Toggling gift type:', giftTypeId, enabled);
    try {
      const response = await adminFutureGiftApi.patch(`/future-gifts/gift-types/${giftTypeId}/toggle`, { enabled });
      console.log('📥 [FutureGiftService] Gift type toggled:', response.data);
      return response.data;
    } catch (error) {
      console.error('💥 [FutureGiftService] Error toggling gift type:', error);
      throw error;
    }
  },

  /**
   * Delete gift type
   */
  deleteGiftType: async (giftTypeId) => {
    console.log('📤 [FutureGiftService] Deleting gift type:', giftTypeId);
    try {
      const response = await adminFutureGiftApi.delete(`/future-gifts/gift-types/${giftTypeId}`);
      console.log('📥 [FutureGiftService] Gift type deleted:', response.data);
      return response.data;
    } catch (error) {
      console.error('💥 [FutureGiftService] Error deleting gift type:', error);
      throw error;
    }
  },

  // ==================== SCHEDULED GIFTS ====================
  /**
   * Get all scheduled gifts
   */
  getScheduledGifts: async (params = {}) => {
    console.log('📤 [FutureGiftService] Fetching scheduled gifts...', params);
    try {
      const response = await adminFutureGiftApi.get('/future-gifts/scheduled', { params });
      console.log('📥 [FutureGiftService] Scheduled gifts fetched:', response.data);
      return response.data;
    } catch (error) {
      console.error('💥 [FutureGiftService] Error fetching scheduled gifts:', error);
      throw error;
    }
  },

  /**
   * Get scheduled gift by ID
   */
  getScheduledGiftById: async (giftId) => {
    console.log('📤 [FutureGiftService] Fetching scheduled gift:', giftId);
    try {
      const response = await adminFutureGiftApi.get(`/future-gifts/scheduled/${giftId}`);
      console.log('📥 [FutureGiftService] Scheduled gift fetched:', response.data);
      return response.data;
    } catch (error) {
      console.error('💥 [FutureGiftService] Error fetching scheduled gift:', error);
      throw error;
    }
  },

  /**
   * Update scheduled gift status
   */
  updateScheduledGiftStatus: async (giftId, status) => {
    console.log('📤 [FutureGiftService] Updating scheduled gift status:', giftId, status);
    try {
      const response = await adminFutureGiftApi.patch(`/future-gifts/scheduled/${giftId}/status`, { status });
      console.log('📥 [FutureGiftService] Scheduled gift status updated:', response.data);
      return response.data;
    } catch (error) {
      console.error('💥 [FutureGiftService] Error updating scheduled gift status:', error);
      throw error;
    }
  },

  /**
   * Cancel scheduled gift
   */
  cancelScheduledGift: async (giftId) => {
    console.log('📤 [FutureGiftService] Cancelling scheduled gift:', giftId);
    try {
      const response = await adminFutureGiftApi.post(`/future-gifts/scheduled/${giftId}/cancel`);
      console.log('📥 [FutureGiftService] Scheduled gift cancelled:', response.data);
      return response.data;
    } catch (error) {
      console.error('💥 [FutureGiftService] Error cancelling scheduled gift:', error);
      throw error;
    }
  },

  // ==================== STATISTICS ====================
  /**
   * Get future gifts statistics
   */
  getStats: async () => {
    console.log('📤 [FutureGiftService] Fetching statistics...');
    try {
      const response = await adminFutureGiftApi.get('/future-gifts/stats');
      console.log('📥 [FutureGiftService] Statistics fetched:', response.data);
      return response.data;
    } catch (error) {
      console.error('💥 [FutureGiftService] Error fetching statistics:', error);
      throw error;
    }
  },

  // ==================== CONFIGURATION ====================
  /**
   * Get complete future gifts configuration
   */
  getFullConfig: async () => {
    console.log('📤 [FutureGiftService] Fetching full configuration...');
    try {
      const response = await adminFutureGiftApi.get('/future-gifts/config');
      console.log('📥 [FutureGiftService] Full configuration fetched:', response.data);
      return response.data;
    } catch (error) {
      console.error('💥 [FutureGiftService] Error fetching full configuration:', error);
      throw error;
    }
  },

  // ==================== UTILITIES ====================
  /**
   * Upload image to Cloudinary
   */
  uploadImageToCloudinary: async (file, uploadPreset = null) => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('upload_preset', uploadPreset || process.env.REACT_APP_CLOUDINARY_UPLOAD_PRESET);
    
    try {
      const response = await axios.post(
        `https://api.cloudinary.com/v1_1/${process.env.REACT_APP_CLOUDINARY_CLOUD_NAME}/image/upload`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data'
          },
          timeout: 30000
        }
      );
      
      return {
        url: response.data.secure_url,
        publicId: response.data.public_id
      };
    } catch (error) {
      console.error('💥 [FutureGiftService] Error uploading to Cloudinary:', error);
      throw error;
    }
  },

  /**
   * Format price
   */
  formatPrice: (price) => {
    return `₦${parseFloat(price || 0).toLocaleString()}`;
  },

  /**
   * Validate ObjectId
   */
  isValidObjectId: (id) => {
    return /^[0-9a-fA-F]{24}$/.test(id);
  },

  /**
   * Format date
   */
  formatDate: (date) => {
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  }
};

export default adminFutureGiftService;